import React, { useRef, useState } from 'react';
import { Box, Button, Chip, Collapse, Link, Stack, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { OwnerInvite, formatInviteDate, hasPortalInvite } from '../admin/inviteStore';
import {
    applicationRowsFromInvite,
    applicationTeasers,
    groupApplicationSections,
} from '../admin/applicationDisplay';
import { AdminInviteActions } from '../admin/useAdminInvites';
import {
    DEPOSIT_STATUSES,
    GENERAL_INVITE_TAGS,
    WAITLIST_TAG,
    selectedInviteTag,
} from '../admin/inviteTags';
import { PLANNED_LITTERS } from '../admin/plannedLitters';
import { abbreviateLastName } from '../admin/personName';
import { mailtoHref, telHref } from '../admin/safeContact';
import { brandColors } from '../Theme';
import { galiaSecondaryButtonSx } from '../theme/galiaUi';

type OwnerInviteCardProps = {
    invite: OwnerInvite;
    actions: AdminInviteActions;
    variant: 'invite' | 'litter';
    onMoveToLitter?: (litterName: string) => void;
    onMoveBack?: () => void;
};

const pillSx = (
    selected: boolean,
    color: { fill: string; hover: string; muted: string }
) => ({
    height: 28,
    borderRadius: 999,
    fontWeight: 600,
    bgcolor: selected ? color.fill : '#fff',
    color: selected ? '#fff' : color.fill,
    borderColor: selected ? color.fill : color.muted,
    '&:hover': {
        bgcolor: selected ? color.hover : `${color.fill}14`,
        borderColor: color.hover,
        color: selected ? '#fff' : color.hover,
    },
});

const reassignSx = {
    textTransform: 'none' as const,
    fontWeight: 600,
    color: brandColors.lavenderDark,
    minHeight: 28,
    px: 1.25,
};

const quietButtonSx = {
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '0.875rem',
    minHeight: 38,
    px: 1.5,
    borderRadius: 1.5,
    borderColor: 'rgba(98, 92, 118, 0.22)',
    color: brandColors.lavenderDark,
    bgcolor: '#fff',
    '&:hover': {
        borderColor: brandColors.lavender,
        bgcolor: 'rgba(128, 121, 154, 0.06)',
    },
};

const OwnerInviteCard: React.FC<OwnerInviteCardProps> = ({
    invite,
    actions,
    variant,
    onMoveToLitter,
    onMoveBack,
}) => {
    const activeInviteTag = selectedInviteTag(invite.tags);
    const invitePills = activeInviteTag
        ? GENERAL_INVITE_TAGS.filter((tag) => tag.id === activeInviteTag)
        : GENERAL_INVITE_TAGS;
    const litterHasWaitlist = (invite.tags || []).indexOf('waitlist') !== -1;
    const litterPills = invite.depositStatus
        ? DEPOSIT_STATUSES.filter((status) => status.id === invite.depositStatus)
        : litterHasWaitlist
          ? [WAITLIST_TAG]
          : [WAITLIST_TAG, ...DEPOSIT_STATUSES];
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [inviting, setInviting] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const application = invite.application;
    const applicationRows = applicationRowsFromInvite(application);
    const teasers = applicationTeasers(applicationRows);
    const sections = groupApplicationSections(applicationRows);
    const appliedLabel = application?.summary ? 'Applied' : 'Invited';
    const emailHref = mailtoHref(invite.email);
    const phoneHref = telHref(invite.phone);

    const handlePdfChosen = async (file: File | undefined) => {
        if (!file) {
            return;
        }
        const looksLikePdf =
            file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        if (!looksLikePdf) {
            setUploadError('Upload a PDF from Jotform.');
            return;
        }
        if (file.size > 3.5 * 1024 * 1024) {
            setUploadError('That PDF is too large. Use a file under 3.5 MB.');
            return;
        }
        setUploadError(null);
        setUploading(true);
        try {
            await actions.uploadApplication(invite.id, file);
            setDetailsOpen(true);
        } catch (error) {
            setUploadError(
                error instanceof Error ? error.message : 'Could not read the application PDF.'
            );
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <Stack
            spacing={1.5}
            sx={{
                p: 2.25,
                border: '1px solid rgba(15, 23, 42, 0.08)',
                borderRadius: 2,
                bgcolor: '#fff',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            }}
        >
            <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827', letterSpacing: '-0.01em' }}>
                    {abbreviateLastName(invite.ownerName)}
                </Typography>
                <Typography sx={{ mt: 0.4, fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.62)' }}>
                    {emailHref ? (
                        <Link href={emailHref} underline="hover" color="inherit">
                            {invite.email}
                        </Link>
                    ) : (
                        invite.email
                    )}
                    {invite.phone ? (
                        <>
                            {' · '}
                            {phoneHref ? (
                                <Link href={phoneHref} underline="hover" color="inherit">
                                    {invite.phone}
                                </Link>
                            ) : (
                                invite.phone
                            )}
                        </>
                    ) : null}
                </Typography>
                <Typography sx={{ mt: 0.35, fontSize: '0.8125rem', color: 'rgba(17, 24, 39, 0.48)' }}>
                    {appliedLabel} {formatInviteDate(invite.createdAt)}
                    {hasPortalInvite(invite) ? ' · Portal invite sent' : ''}
                </Typography>
            </Box>

            {invite.notes && (
                <Typography sx={{ fontSize: '0.9rem', color: 'rgba(17, 24, 39, 0.68)', whiteSpace: 'pre-wrap' }}>
                    {invite.notes}
                </Typography>
            )}

            {teasers.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {teasers.map((teaser) => (
                        <Chip
                            key={teaser}
                            label={teaser}
                            size="small"
                            sx={{
                                height: 'auto',
                                minHeight: 26,
                                borderRadius: 1,
                                bgcolor: 'rgba(128, 121, 154, 0.08)',
                                color: brandColors.lavenderDark,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                maxWidth: '100%',
                                '& .MuiChip-label': {
                                    px: 1,
                                    py: 0.35,
                                    whiteSpace: 'normal',
                                },
                            }}
                        />
                    ))}
                </Box>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                {variant === 'invite' &&
                    invitePills.map((tag) => {
                        const selected = tag.id === activeInviteTag;
                        return (
                            <Chip
                                key={tag.id}
                                label={tag.label}
                                size="small"
                                clickable
                                variant={selected ? 'filled' : 'outlined'}
                                onClick={() => {
                                    if (selected) {
                                        return;
                                    }
                                    void actions.setTag(invite.id, tag.id);
                                }}
                                aria-pressed={selected}
                                sx={pillSx(selected, tag.color)}
                            />
                        );
                    })}
                {variant === 'invite' && activeInviteTag && (
                    <Button type="button" size="small" onClick={() => void actions.clearTags(invite.id)} sx={reassignSx}>
                        Reassign
                    </Button>
                )}
                {variant === 'litter' &&
                    litterPills.map((pill) => {
                        const isWaitlist = pill.id === 'waitlist';
                        const selected = isWaitlist
                            ? litterHasWaitlist && !invite.depositStatus
                            : invite.depositStatus === pill.id;
                        return (
                            <Chip
                                key={pill.id}
                                label={pill.label}
                                size="small"
                                clickable
                                variant={selected ? 'filled' : 'outlined'}
                                onClick={() => {
                                    if (selected) {
                                        return;
                                    }
                                    if (isWaitlist) {
                                        void (async () => {
                                            if (invite.depositStatus) {
                                                await actions.clearDeposit(invite.id);
                                            }
                                            await actions.setTag(invite.id, 'waitlist');
                                        })();
                                        return;
                                    }
                                    void actions.setDeposit(
                                        invite.id,
                                        pill.id as 'pending' | 'paid'
                                    );
                                }}
                                aria-pressed={selected}
                                sx={pillSx(selected, pill.color)}
                            />
                        );
                    })}
                {variant === 'litter' && (invite.depositStatus || litterHasWaitlist) && (
                    <Button
                        type="button"
                        size="small"
                        onClick={() => {
                            if (invite.depositStatus) {
                                void actions.clearDeposit(invite.id);
                                return;
                            }
                            void actions.clearTags(invite.id);
                        }}
                        sx={reassignSx}
                    >
                        Reassign
                    </Button>
                )}
            </Box>

            {onMoveToLitter &&
                PLANNED_LITTERS.map((litter) => (
                    <Button
                        key={litter.id}
                        type="button"
                        variant="outlined"
                        onClick={() => onMoveToLitter(litter.name)}
                        sx={{ ...galiaSecondaryButtonSx, width: '100%' }}
                    >
                        Move to {litter.name}
                    </Button>
                ))}
            {onMoveBack && (
                <Button
                    type="button"
                    variant="outlined"
                    onClick={onMoveBack}
                    sx={{ ...galiaSecondaryButtonSx, width: '100%' }}
                >
                    Move back
                </Button>
            )}

            {applicationRows.length > 0 && (
                <Box>
                    <Button
                        type="button"
                        fullWidth
                        aria-expanded={detailsOpen}
                        onClick={() => setDetailsOpen((open) => !open)}
                        endIcon={detailsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        sx={{
                            justifyContent: 'space-between',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            color: '#111827',
                            bgcolor: detailsOpen ? 'rgba(128, 121, 154, 0.06)' : '#fafafa',
                            border: '1px solid rgba(15, 23, 42, 0.08)',
                            borderRadius: 1.5,
                            py: 1,
                            px: 1.5,
                            '&:hover': { bgcolor: 'rgba(128, 121, 154, 0.08)' },
                        }}
                    >
                        Application details
                    </Button>
                    <Collapse in={detailsOpen} timeout={180}>
                        <Box
                            sx={{
                                mt: 1,
                                px: 1.75,
                                py: 1.75,
                                border: '1px solid rgba(15, 23, 42, 0.06)',
                                borderRadius: 1.5,
                                bgcolor: '#fbfbfc',
                            }}
                        >
                            {sections.map((section, index) => (
                                <Box key={section.title} sx={{ mt: index === 0 ? 0 : 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '0.6875rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            color: brandColors.lavenderDark,
                                            mb: 1,
                                        }}
                                    >
                                        {section.title}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '148px 1fr' },
                                            columnGap: 2,
                                            rowGap: 1.15,
                                        }}
                                    >
                                        {section.rows.map((field) => (
                                            <React.Fragment key={field.label}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.8125rem',
                                                        fontWeight: 600,
                                                        color: 'rgba(17, 24, 39, 0.48)',
                                                        pt: 0.15,
                                                    }}
                                                >
                                                    {field.label}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.9rem',
                                                        color: 'rgba(17, 24, 39, 0.82)',
                                                        whiteSpace: 'pre-wrap',
                                                        lineHeight: 1.45,
                                                    }}
                                                >
                                                    {field.value}
                                                </Typography>
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                            {application?.fileName && (
                                <Typography sx={{ mt: 2, fontSize: '0.75rem', color: 'rgba(17, 24, 39, 0.42)' }}>
                                    Source {application.fileName}
                                    {application.uploadedAt ? ` · ${formatInviteDate(application.uploadedAt)}` : ''}
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </Box>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={(event) => {
                    void handlePdfChosen(event.target.files?.[0]);
                }}
            />
            {uploadError && (
                <Typography sx={{ color: '#9a4a4a', fontSize: '0.875rem' }}>{uploadError}</Typography>
            )}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                {!hasPortalInvite(invite) && (
                    <Button
                        type="button"
                        variant="outlined"
                        disabled={inviting}
                        onClick={() => {
                            setUploadError(null);
                            setInviting(true);
                            void actions
                                .sendPortalInvite(invite.id)
                                .catch((error) => {
                                    setUploadError(
                                        error instanceof Error
                                            ? error.message
                                            : 'Could not email the portal invite.'
                                    );
                                })
                                .finally(() => {
                                    setInviting(false);
                                });
                        }}
                        sx={quietButtonSx}
                    >
                        {inviting ? 'Emailing…' : 'Email portal invite'}
                    </Button>
                )}
                <Button
                    type="button"
                    variant="outlined"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    sx={quietButtonSx}
                >
                    {uploading ? 'Reading PDF…' : applicationRows.length ? 'Replace from PDF' : 'Fill card from PDF'}
                </Button>
                <Button
                    type="button"
                    onClick={() => {
                        if (!window.confirm(`Remove ${abbreviateLastName(invite.ownerName) || invite.email} from the list?`)) {
                            return;
                        }
                        void actions.remove(invite.id);
                    }}
                    sx={{
                        ...reassignSx,
                        ml: 'auto',
                        color: '#9a4a4a',
                        '&:hover': { bgcolor: 'rgba(154, 74, 74, 0.08)' },
                    }}
                >
                    Delete
                </Button>
            </Stack>
        </Stack>
    );
};

export default OwnerInviteCard;
